import React, { useEffect, useMemo, useState } from 'react';

import type { Unit, User } from '../../core/models/data';
import UnitLogoBadge from './common/UnitLogoBadge';
import MintLeafLogo from './icons/AppleLogo';
import {
  buildNextSelectedUnits,
  getUserScopedUnits,
  shouldShowUnitSelectionGate,
  getValidSelectedUnitIds,
} from './unitSelectionGateLogic';

interface UnitSelectionGateProps {
  currentUser: User | null;
  allUnits: Unit[];
  selectedUnits: string[];
  setSelectedUnits: React.Dispatch<React.SetStateAction<string[]>>;
  children: React.ReactNode;
}

const getDisplayFirstName = (user: User | null): string => {
  const firstName = user?.firstName?.trim();
  if (firstName) return firstName;

  const fullName = user?.name?.trim() || '';
  if (fullName) return fullName.split(/\s+/)[0] || '';

  const nickname = user?.nickname?.trim();
  if (nickname) return nickname;

  return '';
};
const isDev = typeof process !== 'undefined' && process.env.NODE_ENV !== 'production';

const UnitSelectionGate: React.FC<UnitSelectionGateProps> = ({
  currentUser,
  allUnits,
  selectedUnits,
  setSelectedUnits,
  children,
}) => {
  const availableUnits = useMemo(() => getUserScopedUnits(allUnits, currentUser), [allUnits, currentUser]);
  const firstName = useMemo(() => getDisplayFirstName(currentUser), [currentUser]);

  const validatedSelectedUnits = useMemo(
    () => getValidSelectedUnitIds(selectedUnits, availableUnits),
    [availableUnits, selectedUnits]
  );

  const [pendingUnitId, setPendingUnitId] = useState<string>(() => validatedSelectedUnits[0] || availableUnits[0]?.id || '');

  const requiresGate = shouldShowUnitSelectionGate({
    isAuthenticated: Boolean(currentUser),
    availableUnits,
    selectedUnits: validatedSelectedUnits,
  });

  useEffect(() => {
    if (!isDev) return;
    console.debug('[unit-gate] selectedUnits', {
      selectedUnitsCount: selectedUnits.length,
      validatedSelectedUnitsCount: validatedSelectedUnits.length,
      availableUnitsCount: availableUnits.length,
    });
    if (requiresGate) {
      console.debug('[unit-gate] showing selector reason', {
        isAuthenticated: Boolean(currentUser),
        availableUnitsCount: availableUnits.length,
        validatedSelectedUnitsCount: validatedSelectedUnits.length,
      });
    } else {
      console.debug('[unit-gate] bypass selector because selectedUnits restored');
    }
  }, [availableUnits, currentUser, requiresGate, selectedUnits, validatedSelectedUnits]);

  useEffect(() => {
    if (pendingUnitId && availableUnits.some(unit => unit.id === pendingUnitId)) return;
    setPendingUnitId(availableUnits[0]?.id || '');
  }, [availableUnits, pendingUnitId]);

  if (!requiresGate) {
    return <>{children}</>;
  }

  const continueDisabled = pendingUnitId.trim().length === 0;

  const handleContinue = () => {
    const nextSelection = buildNextSelectedUnits(pendingUnitId);
    if (nextSelection.length === 0) return;
    setSelectedUnits(nextSelection);
  };

  return (
    <div className="fixed inset-0 z-[200] bg-slate-50 text-slate-900">
      <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col items-center justify-center px-6 py-12 text-center">
        <h1 className="text-3xl font-semibold tracking-tight">
          {firstName ? `Szia ${firstName}! Válassz egységet` : 'Válassz egységet'}
        </h1>
        <p className="mt-3 text-sm text-slate-600">A folytatáshoz válaszd ki, melyik egységgel szeretnél dolgozni.</p>

        {availableUnits.length === 0 ? (
          <div className="mt-10 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-base font-medium">Ehhez a felhasználóhoz nincs hozzárendelt egység.</p>
            <p className="mt-2 text-sm text-slate-500">Kérd az adminisztrátort, hogy rendelje hozzád a megfelelő egységet.</p>
          </div>
        ) : (
          <>
            <div className="mt-10 grid w-full grid-cols-2 gap-5 sm:grid-cols-3">
              {availableUnits.map(unit => {
                const isSelected = pendingUnitId === unit.id;
                return (
                  <button
                    key={unit.id}
                    type="button"
                    onClick={() => setPendingUnitId(unit.id)}
                    className={`group flex flex-col items-center rounded-2xl px-3 py-4 transition-all duration-250 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 ${
                      isSelected
                        ? 'bg-slate-50/70 shadow-[0_0_30px_rgba(16,185,129,0.14),0_10px_28px_rgba(15,23,42,0.08)] hover:shadow-[0_0_36px_rgba(16,185,129,0.2),0_12px_30px_rgba(15,23,42,0.1)]'
                        : 'bg-transparent hover:bg-slate-100/80'
                    }`}
                    aria-pressed={isSelected}
                  >
                    <div
                      className={`relative flex h-24 w-24 items-center justify-center rounded-full transition-all duration-250 ease-out ${
                        isSelected
                          ? 'scale-[1.06] shadow-[0_10px_24px_rgba(15,23,42,0.12)]'
                          : 'scale-100'
                      }`}
                    >
                      {isSelected ? (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-1 -z-10 rounded-full bg-emerald-300/35 blur-xl"
                        />
                      ) : null}
                      <UnitLogoBadge unit={unit} size={88} className="border-0" />
                    </div>
                    <span className="mt-3 max-w-full truncate text-sm font-medium text-slate-700">{unit.name}</span>
                  </button>
                );
              })}
              <div className="flex flex-col items-center rounded-2xl bg-slate-50/50 px-3 py-4 opacity-70">
                <div className="relative flex h-24 w-24 items-center justify-center rounded-full">
                  <MintLeafLogo className="h-[88px] w-[88px] opacity-60 grayscale" />
                </div>
                <span className="mt-3 max-w-full truncate text-sm font-medium text-slate-700">További egység</span>
                <span className="mt-1 text-xs text-slate-500">Egy felhasználóhoz több egység is tartozhat</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleContinue}
              disabled={continueDisabled}
              className="mt-10 inline-flex h-11 min-w-[140px] items-center justify-center rounded-xl bg-slate-900 px-5 text-sm font-semibold text-white transition enabled:hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              Folytatás
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default UnitSelectionGate;
